import type { ReactNode } from 'react';
import styled from 'styled-components';
import { ButtonProps, GhostButton, TertiaryButton } from '@ardoq/button';
import { radius, shadowM, space } from '@ardoq/design-tokens';
import { CloseIcon, Icon, IconName, IconSize } from '@ardoq/icons';
import { ToastType } from './types';
import { perTypeColorMap } from '../const';
import { StatusType } from '../types';

export const ToastSurface = styled.div<{
  $maxWidth: 'fit-content' | undefined;
}>`
  width: fit-content;
  box-shadow: ${shadowM};
  position: relative;
  border-radius: ${radius.r4};
  pointer-events: auto; /* So that children of ToastsContainer can be clicked */
  ${({ $maxWidth }) =>
    $maxWidth === 'fit-content'
      ? 'max-width: fit-content;'
      : 'max-width: 400px;'}
`;

const CloseButton = (props: ButtonProps) => {
  return (
    <GhostButton {...props}>
      <CloseIcon />
    </GhostButton>
  );
};

const NoWrapTertiaryButton = styled(TertiaryButton)`
  white-space: nowrap;
`;

const ToastBody = styled.div<{
  $backgroundColor: string;
  $borderColor: string;
  $textColor: string;
}>`
  display: flex;
  align-items: center;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  border: 1px solid ${({ $borderColor }) => $borderColor};
  color: ${({ $textColor }) => $textColor};
  padding: ${space.medium};
  border-radius: ${radius.r4};
  gap: ${space.medium};
`;

const toastTypeToStatusTypeMap = {
  [ToastType.INFO]: StatusType.INFO,
  [ToastType.SUCCESS]: StatusType.SUCCESS,
  [ToastType.ERROR]: StatusType.ERROR,
};

const iconMap = {
  [ToastType.INFO]: IconName.INFO,
  [ToastType.SUCCESS]: IconName.INFO,
  [ToastType.ERROR]: IconName.ERROR,
};

export type ToastCardProps = {
  type: ToastType;
  message: ReactNode;
  actionButton?: { label: ReactNode; onClick: VoidFunction };
  onClose: VoidFunction;
};

export const ToastCard = (props: ToastCardProps) => {
  const colorMap = perTypeColorMap[toastTypeToStatusTypeMap[props.type]];
  return (
    <ToastBody
      $backgroundColor={colorMap.backgroundColor}
      $borderColor={colorMap.borderColor}
      $textColor={colorMap.labelColor}
    >
      <Icon
        iconName={iconMap[props.type]}
        iconSize={IconSize.MEDIUM}
        color={colorMap.iconColor}
      />
      {props.message}
      {props.actionButton && (
        <NoWrapTertiaryButton onClick={props.actionButton.onClick}>
          {props.actionButton.label}
        </NoWrapTertiaryButton>
      )}
      <CloseButton onClick={props.onClose} />
    </ToastBody>
  );
};

type ToastProps = ToastCardProps & {
  maxWidth?: 'fit-content';
};

export const Toast = ({ maxWidth, ...cardProps }: ToastProps) => (
  <ToastSurface
    $maxWidth={maxWidth}
    data-test-id="toast-notification"
    role="alert"
  >
    <ToastCard {...cardProps} />
  </ToastSurface>
);
